import { Link } from 'react-router-dom'
import type { StoredSession } from '../api/session.js'
import { LEGAL, LegalShell, Mail, TranslationNotice } from './LegalShell.js'

const NOTICE_BEFORE = 'यह हिन्दी अनुवाद केवल सुविधा के लिए है। किसी भी अंतर की स्थिति में'
const NOTICE_LINK = 'फ़्रेंच संस्करण'
const NOTICE_AFTER = 'ही मान्य होगा।'

export function LegalNoticeHi({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="क़ानूनी जानकारी" title="क़ानूनी सूचना">
      <TranslationNotice original="/mentions-legales" before={NOTICE_BEFORE} link={NOTICE_LINK} after={NOTICE_AFTER} />

      <h2>प्रकाशक</h2>
      <p>
        वेबसाइट {LEGAL.domain} (“{LEGAL.siteName}”) एक निजी व्यक्ति द्वारा, ग़ैर-पेशेवर तौर पर, प्रकाशित की जाती है। डिजिटल अर्थव्यवस्था में
        विश्वास से संबंधित 21 जून 2004 के फ़्रांसीसी क़ानून संख्या 2004-575 के अनुच्छेद 6 III-2 के अनुसार, प्रकाशक अपनी निजी जानकारी
        प्रकाशित नहीं करता; यह जानकारी होस्टिंग प्रदाता को दी गई है।
      </p>
      <p>
        संपर्क: <Mail />
      </p>

      <h2>होस्टिंग</h2>
      <p>
        यह वेबसाइट {LEGAL.host.name}, {LEGAL.host.address.hi} द्वारा होस्ट की जाती है —{' '}
        <a href={LEGAL.host.website} rel="noopener noreferrer" target="_blank">
          {LEGAL.host.website.replace('https://', '')}
        </a>
        ।
      </p>

      <h2>बौद्धिक संपदा</h2>
      <p>
        वेबसाइट का कोड, चित्र, लोगो और टेक्स्ट प्रकाशक की संपत्ति हैं, जब तक कि अलग से न बताया गया हो। शतरंज इंजन ओपन-सोर्स लाइब्रेरी
        chess.js (BSD-2-Clause लाइसेंस) पर आधारित है; Fraunces और Instrument Sans फ़ॉन्ट SIL Open Font License के तहत वितरित किए जाते हैं।
      </p>
      <p>
        Chess-Uno एक स्वतंत्र गेम है। यह UNO® ट्रेडमार्क के मालिक Mattel से न तो संबद्ध है, न उसके द्वारा समर्थित है, न ही प्रायोजित।
      </p>

      <h2>व्यक्तिगत डेटा</h2>
      <p>
        खिलाड़ियों के डेटा को कैसे प्रोसेस किया जाता है, इसका विवरण <Link to="/hi/privacy">गोपनीयता नीति</Link> में दिया गया है।
      </p>
    </LegalShell>
  )
}

export function PrivacyHi({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="आपका डेटा" title="गोपनीयता नीति">
      <TranslationNotice original="/confidentialite" before={NOTICE_BEFORE} link={NOTICE_LINK} after={NOTICE_AFTER} />
      <p className="lead-p">
        {LEGAL.siteName} सिर्फ़ उतनी ही जानकारी इकट्ठा करता है जितनी आपको खेलने और रैंकिंग में आने के लिए ज़रूरी है। कोई विज्ञापन नहीं, ऑडियंस
        मापने का कोई थर्ड-पार्टी टूल नहीं, कोई ट्रैकिंग कुकी नहीं।
      </p>

      <h2>डेटा नियंत्रक</h2>
      <p>
        वेबसाइट का प्रकाशक, एक निजी व्यक्ति (<Link to="/hi/legal-notice">क़ानूनी सूचना</Link> देखें), जिनसे <Mail /> पर संपर्क किया जा सकता है।
      </p>

      <h2>प्रोसेस किया जाने वाला डेटा</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>डेटा</th>
              <th>उपयोग</th>
              <th>कब तक रखा जाता है</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>यूज़रनेम</td>
              <td>आपके अकाउंट की पहचान करना और आपको दूसरे खिलाड़ियों को दिखाना।</td>
              <td>अकाउंट डिलीट होने तक।</td>
            </tr>
            <tr>
              <td>पासवर्ड</td>
              <td>लॉग इन करना। इसे कभी भी सादे टेक्स्ट में सेव नहीं किया जाता: सिर्फ़ उसका सॉल्टेड हैश (scrypt) रखा जाता है।</td>
              <td>अकाउंट डिलीट होने तक।</td>
            </tr>
            <tr>
              <td>सेशन टोकन</td>
              <td>लॉग इन बने रहना। टोकन आपके ब्राउज़र के लोकल स्टोरेज में रखा जाता है; सर्वर उसका सिर्फ़ हैश रखता है।</td>
              <td>लॉग आउट करने या अकाउंट डिलीट होने तक।</td>
            </tr>
            <tr>
              <td>गेम</td>
              <td>हर गेम की चालें, खींचे गए कार्ड और लॉग, ताकि डिस्कनेक्शन के बाद गेम फिर से शुरू किया जा सके और विवादों का निपटारा हो सके।</td>
              <td>अकाउंट डिलीट होने तक।</td>
            </tr>
            <tr>
              <td>रैंक्ड नतीजे</td>
              <td>Elo रेटिंग, आँकड़े, आमने-सामने के मुक़ाबलों का रिकॉर्ड।</td>
              <td>अकाउंट डिलीट होने तक, फिर आपके यूज़रनेम के बिना रखे जाते हैं ताकि दूसरे खिलाड़ियों की रैंकिंग न बिगड़े।</td>
            </tr>
            <tr>
              <td>टूर्नामेंट</td>
              <td>टूर्नामेंट का नाम, प्रतिभागियों के यूज़रनेम, ब्रैकेट और मैचों के नतीजे, टूर्नामेंट आयोजित करने के लिए।</td>
              <td>अकाउंट डिलीट होने तक।</td>
            </tr>
            <tr>
              <td>फ़ीडबैक</td>
              <td>
                जो आप भेजना चुनते हैं: संदेश, 1 से 5 तक की रेटिंग, वैकल्पिक संपर्क (ईमेल या मैसेजिंग यूज़रनेम), साथ ही अगर आप लॉग इन हैं तो आपका
                यूज़रनेम, और इस्तेमाल किया गया पेज, भाषा, स्क्रीन का प्रकार और ब्राउज़र। प्रकाशक इसे गेम बेहतर बनाने के लिए और, आपके कहने पर, आपको
                जवाब देने के लिए पढ़ता है।
              </td>
              <td>12 महीने, या जब तक आप इसे डिलीट करने का अनुरोध न करें।</td>
            </tr>
            <tr>
              <td>विज़िट कहाँ से आती हैं</td>
              <td>
                यह जानना कि विज़िटर Instagram, Google या कहीं और से आते हैं: रेफ़र करने वाली साइट (सिर्फ़ डोमेन नाम), लिंक के कैंपेन टैग, बिना
                पैरामीटर या इनवाइट कोड के लैंडिंग पेज, भाषा, स्क्रीन का प्रकार, और क्या उस विज़िट से कोई अकाउंट बना। कोई IP पता, ब्राउज़र की विस्तृत
                जानकारी या स्थायी पहचानकर्ता दर्ज नहीं किया जाता, और कुछ भी आपके अकाउंट से नहीं जोड़ा जाता। अगर आपका ब्राउज़र “Do Not Track” या
                Global Privacy Control सिग्नल भेजता है, तो कुछ भी नहीं मापा जाता।
              </td>
              <td>13 महीने।</td>
            </tr>
            <tr>
              <td>IP पता</td>
              <td>लॉग इन की कोशिशों और फ़ीडबैक भेजने को सीमित करना (हमलों से सुरक्षा)। मेमोरी में रखा जाता है, गेम इसे कभी दर्ज नहीं करता।</td>
              <td>लॉग इन के लिए एक मिनट, फ़ीडबैक के लिए दस मिनट। होस्टिंग प्रदाता के तकनीकी लॉग उसकी अपनी नीति के अनुसार रखे जाते हैं।</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>क़ानूनी आधार</h2>
      <ul>
        <li>
          <strong>सेवा का प्रदर्शन</strong>, जिसका अनुरोध आप अकाउंट बनाकर करते हैं: अकाउंट, गेम, रैंकिंग।
        </li>
        <li>
          <strong>वैध हित</strong>: सेवा की सुरक्षा (लॉग इन की कोशिशों और फ़ीडबैक भेजने को सीमित करना), आपके भेजे गए फ़ीडबैक से गेम को बेहतर
          बनाना, और विज़िट कहाँ से आती हैं इसका सांख्यिकीय माप। आप अपने ब्राउज़र में “Do Not Track” चालू करके कभी भी इस पर आपत्ति कर सकते हैं।
        </li>
      </ul>

      <h2>क्या सार्वजनिक है</h2>
      <p>
        आपका यूज़रनेम, Elo रेटिंग और स्तर, आँकड़े, रैंक्ड नतीजे और आमने-सामने के मुक़ाबलों का रिकॉर्ड रैंकिंग में और आपकी प्रोफ़ाइल पर सबको
        दिखते हैं। AI के ख़िलाफ़ गेम रैंक नहीं होते। किसी टूर्नामेंट का नाम, उसके प्रतिभागियों के यूज़रनेम, उसका ब्रैकेट और उसके नतीजे हर उस
        व्यक्ति को दिखते हैं जिसे उसका कोड पता है। आपका फ़ीडबैक कभी प्रकाशित नहीं किया जाता।
      </p>

      <h2>आपके ब्राउज़र में स्टोरेज</h2>
      <p>
        वेबसाइट कुकीज़ का इस्तेमाल नहीं करती। यह ब्राउज़र के लोकल स्टोरेज में आपको लॉग इन रखने के लिए आपका सेशन टोकन, आपकी डिस्प्ले
        प्राथमिकताएँ (भाषा, आवाज़) और एक छोटा काउंटर सेव करती है, जो गेम के बाद आपसे बार-बार फ़ीडबैक माँगने से बचाता है: यह स्टोरेज सेवा के लिए
        पूरी तरह ज़रूरी है, इसलिए इसके लिए सहमति की ज़रूरत नहीं है। टैब का सेशन स्टोरेज, टैब बंद होने तक, विज़िट का एक रैंडम पहचानकर्ता रखता है
        ताकि उसे सिर्फ़ एक बार गिना जाए; यह एक विज़िट से दूसरी विज़िट तक नहीं रखा जाता।
      </p>

      <h2>प्राप्तकर्ता और प्रोसेसर</h2>
      <p>आपका डेटा न तो बेचा जाता है और न ही व्यावसायिक उद्देश्यों के लिए साझा किया जाता है। इसे ये प्रोसेस करते हैं:</p>
      <ul>
        <li>
          <strong>{LEGAL.host.name}</strong> (संयुक्त राज्य अमेरिका): सर्वर और डेटाबेस की होस्टिंग;
        </li>
        <li>
          <strong>Cloudflare, Inc.</strong> (संयुक्त राज्य अमेरिका): डोमेन नाम का प्रबंधन और, जहाँ लागू हो, ट्रैफ़िक की रूटिंग;
        </li>
        <li>
          <strong>Telegram</strong> (मैसेजिंग) और <strong>Resend, Inc.</strong> (संयुक्त राज्य अमेरिका, ईमेल भेजना), जहाँ लागू हो: मिलने वाले हर
          फ़ीडबैक की सूचना प्रकाशक तक पहुँचाना।
        </li>
      </ul>
      <p>
        यूरोपीय संघ के बाहर होने वाले ये ट्रांसफ़र GDPR में दिए गए सुरक्षा उपायों के तहत आते हैं (प्रदाता के अनुसार, यूरोपीय आयोग के मानक
        संविदात्मक खंड या EU–US Data Privacy Framework)।
      </p>

      <h2>सुरक्षा</h2>
      <p>
        एन्क्रिप्टेड कनेक्शन (HTTPS), पासवर्ड और टोकन हैश के रूप में सेव, लॉग इन की सीमित कोशिशें, डेटाबेस तक पहुँच सिर्फ़ होस्टिंग
        एनवायरनमेंट तक सीमित।
      </p>

      <h2>आपके अधिकार</h2>
      <p>
        आपको अपने डेटा तक पहुँच, उसे सुधारने, मिटाने, उसकी प्रोसेसिंग सीमित करने, उसे पोर्ट करने और उसकी प्रोसेसिंग पर आपत्ति करने का अधिकार
        है। इन अधिकारों का इस्तेमाल करने के लिए — ख़ास तौर पर अपना अकाउंट डिलीट करने के लिए — ऐसे पते से जिससे आपकी पहचान हो सके, अपना
        यूज़रनेम बताते हुए <Mail /> पर लिखें। आपको एक महीने के भीतर जवाब मिलेगा।
      </p>
      <p>
        आप फ़्रांस के डेटा संरक्षण प्राधिकरण CNIL के पास शिकायत भी दर्ज करा सकते हैं (
        <a href="https://www.cnil.fr" rel="noopener noreferrer" target="_blank">
          cnil.fr
        </a>
        )।
      </p>
    </LegalShell>
  )
}
